import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import lottie from "lottie-web";
import { Helmet } from "react-helmet-async";

import Post from "../components/Post";
import Footer from "../components/Footer";
import ReportFab from "../components/ReportFab";
import CategoryStrip from "../components/CategoryStrip";
import HeroBubbles from "../components/HeroBubbles";
import RvrRoadmap from "../components/RvrRoadmap";
import TopContributors from "../components/TopContributors";
import ParentBrand from "../components/ParentBrand";
import { prefersReducedMotion } from "../utils/motion";
import { HERO_GRADFLOW_CONFIG } from "../utils/heroGradient";
import lightninganimation from "../components/lottie/lightning.json";
import { GradFlow } from "gradflow";

const PREVIEW_SIZE = 8;

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.acha.BLEEP";
const APP_STORE_URL =
  "https://apps.apple.com/ca/app/bleep-report-now/id6751865451";

const Home = ({ domain, userData }) => {
  const [userDataState, setUserDataState] = useState(userData);
  useEffect(() => setUserDataState(userData), [userData]);

  const [userId, userType, userName, userEmail, , , , userAnonymous] =
    userDataState;

  const canReport = userId == null || userType === "user";

  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [showFab, setShowFab] = useState(false);
  const [reduceMotion] = useState(prefersReducedMotion);

  const lightningRef = useRef(null);

  // One definition, used for both the greeting and the identicon seed, so the
  // avatar always belongs to the name printed beside it.
  const displayName = useMemo(() => {
    if (userAnonymous === "true") {
      return userId ? `Bleeper#${String(userId).slice(-6)}` : "Bleeper";
    }
    return userName || userEmail || userId || "bleep-user";
  }, [userAnonymous, userId, userName, userEmail]);

  const dicebearUrl = `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(
    displayName,
  )}`;

  /* ---------------- Feed preview: two rows, one request -------------------- */
  const loadPreview = React.useCallback(() => {
    setPostsLoading(true);
    fetch(
      `/api/posts?page=1&size=${PREVIEW_SIZE}` +
        (userId ? `&user=${userId}` : ""),
    )
      .then((res) => res.json())
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error fetching posts:", err))
      .finally(() => setPostsLoading(false));
  }, [userId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  /* ---------------- Lightning accent on the CTA ---------------------------- */
  useEffect(() => {
    if (reduceMotion) return;
    const container = lightningRef.current;
    if (!container || container._loaded) return;
    container._loaded = true;
    lottie.loadAnimation({
      container,
      renderer: "canvas",
      loop: true,
      autoplay: true,
      animationData: lightninganimation,
    });
    lottie.setSpeed(0.6);
  }, [reduceMotion, canReport]);

  /* ---------------- Floating report button --------------------------------- */
  useEffect(() => {
    const onScroll = () => setShowFab(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const trackEvent = (action) => {
    fetch("/api/trackers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user: userId, action }),
    }).catch((error) => console.error("Error tracking event:", error));
  };

  // Height comes from .home-badges img in the stylesheet.
  const storeBadges = (
    <>
      <a
        onClick={() => trackEvent("Clicked Play Store Link")}
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src="/images/getitongoogleplaybadge.png"
          alt="Get it on Google Play"
        />
      </a>
      <a
        onClick={() => trackEvent("Clicked App Store Link")}
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src="/images/appstore_btn.png" alt="Download on the App Store" />
      </a>
    </>
  );

  return (
    <div className="bleep-light bl-page">
      <Helmet>
        <title>BLEEP - Suspect a counterfeit? Report Now!</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services. Empowering users to report and verify the authenticity of goods, BLEEP connects you directly with brands and enforcement agents."
        />
      </Helmet>

      {/* ---------------------------------------------------------------- Hero */}
      <section className="bl-hero home-hero">
        <GradFlow className="bl-hero__gradflow" config={HERO_GRADFLOW_CONFIG} />
        {/* Sibling of the shell, not a child: .bl-shell caps at 1200px and
            would strangle the rails on wide screens. */}
        <HeroBubbles />
        <div className="bl-shell">
          <div className="bl-hero__copy">
            {/* An acknowledgement, not a profile card. The navbar's profile
                button already carries the avatar, the account type and the
                email, so repeating them here only pushed the headline down. */}
            {userEmail && (
              <Link
                to="/me"
                className="welcome-pill"
                aria-label="Go to your profile"
              >
                <img className="welcome-pill__orb" src={dicebearUrl} alt="" />
                <span className="welcome-pill__text">
                  Welcome back, <strong>{displayName}</strong>
                </span>
                {userType !== "user" && (
                  <span className="welcome-pill__badge" title="Account type">
                    {userType}
                  </span>
                )}
              </Link>
            )}

            <h1 className="home-title">
              <span className="home-title__lead">Suspect a counterfeit?</span>
              <span className="home-title__accent">BLEEP it.</span>
            </h1>

            <p className="home-sub">
              Report a fake product in under a minute. We take it to the brands
              and agencies who can act.
            </p>

            {/* Takes over from the side rails below 1280px, where the gutters
                have collapsed. CSS shows exactly one of the two. */}
            <HeroBubbles variant="inline" />

            {canReport && (
              <div className="home-cta-row">
                <span className="home-cta-anchor">
                  {!reduceMotion && (
                    <span
                      className="home-lightning"
                      ref={lightningRef}
                      aria-hidden="true"
                    />
                  )}
                  <button
                    type="button"
                    className="bleep-btn-report bleep-btn-report--pulse"
                    onClick={() => navigate("/report")}
                  >
                    <i className="fa-solid fa-bolt-lightning" />
                    REPORT NOW
                  </button>
                </span>
              </div>
            )}

            <div className="home-badges">{storeBadges}</div>
          </div>
        </div>
      </section>

      {/* ------------------------------- Browse by sector + latest bleeps ----
          One dark band: the cards are dark, so the strip and the feed share a
          surface instead of sitting on separate light slabs. */}
      <section className="bl-section bl-section--dark bl-section--strip-first">
        <div className="bl-strip-wrap">
          <CategoryStrip />
        </div>

        <div className="bl-shell">
          <div className="bl-section-head bl-section-head--spaced">
            <h2 className="bl-section-title">Latest BLEEPs</h2>
            <Link to="/bleeps" className="bleep-link-quiet">
              View all
              <i className="fas fa-arrow-right" />
            </Link>
          </div>

          {postsLoading ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3">
              {Array.from({ length: PREVIEW_SIZE }).map((_, i) => (
                <div className="col pb-3" key={i}>
                  <div className="bleep-post-skeleton bl-pulse">
                    <div className="bleep-post-skeleton__media" />
                    <div className="bleep-post-skeleton__body">
                      <div className="bleep-post-skeleton__line" />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "80%" }}
                      />
                      <div
                        className="bleep-post-skeleton__line"
                        style={{ width: "45%" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="home-empty">
              <p style={{ marginBottom: 20 }}>
                No bleeps yet. Be the first to report one.
              </p>
              {canReport && (
                <button
                  type="button"
                  className="bleep-btn-report bleep-btn-report--sm"
                  onClick={() => navigate("/report")}
                >
                  <i className="fa-solid fa-bolt-lightning" />
                  REPORT NOW
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3">
                {posts.map((post) => (
                  <Post key={post._id} post={post} />
                ))}
              </div>
              <Link to="/bleeps" className="home-seeall">
                View all BLEEPs
                <span className="home-seeall__arrow">
                  <i className="fas fa-arrow-right" />
                </span>
              </Link>
            </>
          )}
        </div>
      </section>

      {/* --------------------------------------------------- Top contributors */}
      {/* Renders nothing at all when nobody has posted this month, so Home runs
          the feed straight into "How BLEEP works" as it did before. */}
      <TopContributors />

      {/* ------------------------------------------------------- How it works */}
      <section className="bl-section">
        <div className="bl-shell">
          <div className="bl-section-head">
            <div>
              <h2 className="bl-section-title">How BLEEP works</h2>
              <p className="bl-section-lead">
                RVR: Report, Verify and Remedial Action
              </p>
            </div>
            <Link to="/about" className="bleep-link-quiet">
              Read more
              <i className="fas fa-arrow-right" />
            </Link>
          </div>
          <RvrRoadmap />
        </div>
      </section>

      {canReport && (
        <ReportFab visible={showFab} onClick={() => navigate("/report")} />
      )}

      <ParentBrand />

      <Footer domain={domain} />
    </div>
  );
};

export default Home;
